import React, { Component } from 'react';
import { isValidHin } from '../../../util/marineService';
import CheckInStepHeader from './CheckInStepHeader';

/**
 * Step 1 of check-in for a BOAT: capture the Hull Identification Number.
 *
 * Deliberately NOT a branch inside `Vinstep.jsx`. That component is 610 lines of
 * barcode scanner, NHTSA decode, five-level trim matching, a mismatch
 * reconciliation UI and a duplicate-VIN-in-shop UI. A HIN needs none of it.
 *
 * No barcode scanner here either: HINs are stamped or engraved into the transom,
 * not barcoded. There is nothing for a camera to read. That omission is
 * deliberate, not a gap.
 *
 * No API call. `VINStep` posts to /vin/verify because a VIN decode rewrites
 * year/make/model server-side and needs reconciliation. A HIN decodes to
 * nothing, so the value rides in wizard state and is written once at
 * `completeCheckin` — which also means a cancelled check-in leaves no partial
 * write, matching how photos and the signature already behave.
 */
class HinStep extends Component {
  constructor(props) {
    super(props);

    const existing = String(props.hin || '').trim().toUpperCase();

    this.state = {
      hin: existing,
      // 'prefilled' — a HIN is already on this boat from an earlier visit.
      // 'editing'   — typing a new one, or changing the prefilled one.
      mode: existing ? 'prefilled' : 'editing',
      touched: false,
      showOverride: false,
    };
  }

  handleChange = (event) => {
    this.setState({
      hin: event.target.value.toUpperCase(),
      touched: true,
      showOverride: false,
    });
  };

  // Confirming a prefilled HIN PRESERVES the stored verified flag rather than
  // setting it true. A HIN recorded via the override on an earlier visit stays
  // unverified — otherwise one tap would launder it into a verified value.
  // Re-validating requires "Change", which runs the pattern normally.
  handleConfirmExisting = () => {
    this.props.onHinConfirmed(this.state.hin, this.props.hinVerified === true);
  };

  handleChangeExisting = () => {
    this.setState({ mode: 'editing', touched: false, showOverride: false });
  };

  handleContinue = () => {
    this.props.onHinConfirmed(this.state.hin.trim(), true);
  };

  handleOverrideConfirmed = () => {
    this.props.onHinConfirmed(this.state.hin.trim(), false);
  };

  renderHelp() {
    return (
      <div className="card bg-light mt-4">
        <div className="card-body">
          <h6 className="card-title">
            <i className="fa-sharp fa-regular fa-info-circle text-primary mr-2"></i>
            Where to find the HIN:
          </h6>
          <ul className="mb-0 small">
            <li><strong>Transom, upper starboard corner</strong> — the primary location</li>
            <li>A duplicate is often hidden inside the hull or under a fitting</li>
            <li>Registration and title documents</li>
          </ul>
        </div>
      </div>
    );
  }

  renderPrefilled() {
    return (
      <>
        <div className="form-group">
          <label className="font-weight-bold">Hull Identification Number on file</label>
          <div
            className="form-control form-control-lg text-center bg-light"
            style={{ letterSpacing: '2px', fontFamily: 'monospace', fontSize: '1.2rem' }}
          >
            {this.state.hin}
          </div>
        </div>

        {this.props.hinVerified !== true && (
          <div className="alert alert-warning" role="alert">
            <i className="fa-sharp fa-regular fa-triangle-exclamation mr-2"></i>
            This HIN was recorded without matching the standard format.
          </div>
        )}

        <p className="text-muted small text-center">
          Check this against the hull before confirming.
        </p>

        <div className="text-center mt-4">
          <button
            className="btn btn-outline-secondary btn-lg mr-3"
            onClick={this.handleChangeExisting}
            disabled={this.props.loading}
          >
            <i className="fa-sharp fa-regular fa-pen mr-2"></i>
            Change
          </button>
          <button
            className="btn btn-primary btn-lg"
            onClick={this.handleConfirmExisting}
            disabled={this.props.loading}
          >
            <i className="fa-sharp fa-regular fa-check-circle mr-2"></i>
            CONFIRM &amp; CONTINUE
          </button>
        </div>
      </>
    );
  }

  renderEditing() {
    const trimmed = this.state.hin.trim();
    const valid = isValidHin(trimmed);
    // Blank is NOT an override case — it is simply incomplete. The override
    // exists only for a real hull whose real HIN does not match the format.
    const failing = this.state.touched && trimmed !== '' && !valid;

    return (
      <>
        <div className="form-group">
          <label htmlFor="hin-input" className="font-weight-bold">
            Hull Identification Number
          </label>
          <input
            id="hin-input"
            type="text"
            className={`form-control form-control-lg text-center ${failing ? 'is-invalid' : ''}`}
            value={this.state.hin}
            onChange={this.handleChange}
            placeholder="e.g. BUJ31234A323"
            maxLength={12}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            disabled={this.props.loading}
            style={{ letterSpacing: '2px', fontFamily: 'monospace', fontSize: '1.2rem' }}
          />
        </div>

        {failing && !this.state.showOverride && (
          <div className="alert alert-danger" role="alert">
            <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
            That is not a standard 12-character HIN. Expected 3 letters, then 5 letters or
            numbers, then a month letter A&ndash;L, then 3 digits &mdash; with no I, O or Q.
            <div className="mt-3">
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => this.setState({ showOverride: true })}
                disabled={this.props.loading}
              >
                Use this HIN anyway
              </button>
            </div>
          </div>
        )}

        {failing && this.state.showOverride && (
          <div className="alert alert-warning" role="alert">
            <i className="fa-sharp fa-regular fa-triangle-exclamation mr-2"></i>
            <strong>Record <span className="text-monospace">{trimmed}</span> as entered?</strong>
            <p className="mb-3 mt-2 small">
              Older and imported hulls often do not follow the standard format. It will be saved
              and flagged as unverified.
            </p>
            <button
              className="btn btn-outline-secondary btn-sm mr-2"
              onClick={() => this.setState({ showOverride: false })}
              disabled={this.props.loading}
            >
              Go back
            </button>
            <button
              className="btn btn-warning btn-sm"
              onClick={this.handleOverrideConfirmed}
              disabled={this.props.loading}
            >
              Yes, use it
            </button>
          </div>
        )}

        <div className="text-center mt-4">
          <button
            className="btn btn-primary btn-lg"
            onClick={this.handleContinue}
            disabled={!valid || this.props.loading}
          >
            {this.props.loading && (
              <span className="mr-2">
                <i className="fa-sharp fa-regular fa-refresh fa-spin" />
              </span>
            )}
            CONTINUE
          </button>
        </div>
      </>
    );
  }

  render() {
    return (
      <div className="hin-step">
        <CheckInStepHeader
          stepNumber={this.props.stepNumber}
          totalSteps={this.props.totalSteps}
          title="Verify HIN"
        />

        <div className="row justify-content-center">
          <div className="col-md-8">
            {this.state.mode === 'prefilled' ? this.renderPrefilled() : this.renderEditing()}
            {this.renderHelp()}
          </div>
        </div>
      </div>
    );
  }
}

export default HinStep;
